import { Box, Checkbox, FormControlLabel } from "@mui/material"
import { ListItemBox } from "lib/@components/ListItemBox"
import { If } from "lib/@components/switch"
import { prevent } from "lib/prevent"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { idIndex } from "routes/schedule/lib/getTreeItemIndex"
import { useTreeItemIndex } from "routes/schedule/lib/useTreeItemIndex"
import { selectItems, useSelection } from "routes/schedule/lib/useSelection"
import {
    setRetiredSelection,
    useRetiredSelection,
} from "routes/schedule/my-schedules/my-custom-schedules/retired-selection"
import { SelectionActions } from "routes/schedule/my-schedules/my-custom-schedules/selection-actions"
import { getSchedulesRegimeUsage } from "routes/schedule/my-schedules/my-custom-schedules/controller/get-schedules-regime-usage"

export function SelectAllSchedules({ drafts = true, published = true, retired = false }) {
    const treeIndex = useTreeItemIndex()
    const [search] = useSearchParam("search", "")
    const [isGlobalSelected] = useSelection()
    const [, , isRetiredSelected] = useRetiredSelection()

    const matches = getMatches()
    const isSelected = retired ? isRetiredSelected : isGlobalSelected
    const selectedItems = matches.filter((m) => isSelected(m._id))
    const allSelected = matches.length > 0 && selectedItems.length === matches.length
    const someSelected = selectedItems.length > 0 && !allSelected

    const publishedItems = selectedItems.filter((m) => !m.isDraft && !m.isRetired)
    const draftItems = selectedItems.filter((m) => m.isDraft)
    const retiredItems = selectedItems.filter((m) => m.isRetired)
    const regimeUsage = getSchedulesRegimeUsage.useResults(retiredItems.map((m) => m._id)) ?? []
    const usedCount = regimeUsage.length
    const summaryText = summary()

    if (matches.length === 0) return null

    return (
        <ListItemBox
            sx={{ px: 2, py: 0.5, ...(selectedItems.length > 0 && { bgcolor: "action.hover", borderRadius: 1 }) }}
        >
            <FormControlLabel
                label={
                    <Box component="span" sx={{ typography: "body2" }}>
                        <Box component="span" sx={{ fontWeight: selectedItems.length > 0 ? 600 : 400 }}>
                            {summaryText.primary}
                        </Box>
                        {summaryText.secondary && (
                            <Box component="span" sx={{ ml: 1, color: "text.secondary" }}>
                                {summaryText.secondary}
                            </Box>
                        )}
                    </Box>
                }
                control={
                    <Checkbox
                        data-cy="select-all-schedules"
                        checked={allSelected}
                        indeterminate={someSelected}
                        disabled={matches.length === 0}
                        onClick={prevent(toggleAll)}
                    />
                }
            />
            <Box flex={1} />
            <If truthy={selectedItems.length}>
                <SelectionActions
                    publishedItems={publishedItems}
                    draftItems={draftItems}
                    retiredItems={retiredItems}
                    regimeUsage={regimeUsage}
                />
            </If>
        </ListItemBox>
    )

    function summary() {
        const n = selectedItems.length
        if (n === 0) return { primary: "Select all", secondary: "" }
        if (retired)
            return { primary: `${n} selected`, secondary: usedCount > 0 ? `${usedCount} used by a regime` : "" }
        if (publishedItems.length && draftItems.length) {
            return {
                primary: `${n} selected`,
                secondary: `${publishedItems.length} published · ${draftItems.length} ${"draft".pluralize(draftItems.length)}`,
            }
        }
        if (publishedItems.length) return { primary: `${publishedItems.length} published selected`, secondary: "" }
        return { primary: `${draftItems.length} ${"draft".pluralize(draftItems.length)} selected`, secondary: "" }
    }

    function getMatches() {
        const term = search.trim().toLowerCase()
        return Object.values(treeIndex?.[idIndex] ?? {})
            .filter((list) => list.some((s) => s.custom))
            .map(toMatch)
            .filter((m) => (m.isRetired ? retired : m.isDraft ? drafts : published) && (!term || m.text.includes(term)))
    }

    function toMatch(list) {
        const isRetired = list.some((s) => s.isRetired)
        const isDraft = !isRetired && list.some((s) => s.isDraft)
        const item = list.find((s) => s.custom) ?? list[0]
        return {
            _id: item._id,
            id: item.id,
            isRetired,
            isDraft,
            label: item.label ?? item.code ?? "",
            text: `${item.label ?? ""} ${item.code ?? ""}`.toLowerCase(),
        }
    }

    function toggleAll() {
        const setter = retired ? setRetiredSelection : selectItems
        setter((current) => {
            const next = { ...current }
            for (const m of matches) {
                if (allSelected) delete next[m._id]
                else next[m._id] = retired ? true : [m.id]
            }
            return next
        })
    }
}
